import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from '@app/core/http/api.service';
import { IGraphUser, IOcbCULocalAgent, IOcbCULocalAgentExecutionTrigger, IOcbCULocalAgentMaintenance, IOcbLocalAgent, IOcbLocalAgentCommand, IOcbLocalAgentExecutionTrigger, IOcbLocalAgentMaintenance } from '@app/core/models';
import { environment } from '@env/environment';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

@Injectable()
export class LocalAgentsService extends ApiService {

    private localAgentsUrl: string;

    private options = {
        headers: this.defaultHeaders,
        observe: 'body' as 'body',
    };

    constructor(httpClient: HttpClient) {
        super(httpClient);

        if (!environment.api.endpoint) {
            throw new Error('Could not initialize the "local-agents" service, the proxyPath configuration is invalid.');
        }

        this.localAgentsUrl = `${environment.api.endpoint}/LocalAgents`;
    }

    /**
     * Get the list of local agents
     * @param environmentId Internal identifier of the environment
     * @param includeDisabled {true} to return disabled local agents
     */
    public getLocalAgents(environmentId?: string, includeDisabled?: boolean): Observable<IOcbLocalAgent[]> {

        let url = `${this.localAgentsUrl}?`;

        if (environmentId) { url = `${url}EnvironmentId=${environmentId}&`; }
        if (includeDisabled) { url = `${url}IncludeDisabled=${includeDisabled}`; }

        return this.httpClient.get<IOcbLocalAgent[]>(url);
    }

    /**
     * Add a local agent
     * @param localAgent Local agent to create
     */
    public addLocalAgent(localAgent: IOcbCULocalAgent): Observable<IOcbLocalAgent> {
        return this.httpClient.post<IOcbLocalAgent>(this.localAgentsUrl, localAgent, this.options);
    }

    /**
     * Get a local agent
     * @param localAgentId Internal identifier of the local agent
     */
    public getLocalAgent(localAgentId: string): Observable<IOcbLocalAgent> {
        const url = `${this.localAgentsUrl}/${localAgentId}`;
        return this.httpClient.get<IOcbLocalAgent>(url, this.options);
    }

    /**
     * Update a local agent
     * @param localAgentId Internal identifier of the local agent
     * @param localAgent Data to update
     */
    public updateLocalAgent(localAgentId: string, localAgent: IOcbCULocalAgent): Observable<void> {
        const url = `${this.localAgentsUrl}/${localAgentId}`;
        return this.httpClient.put<void>(url, localAgent, this.options);
    }

    /**
     * Delete a local agent
     * @param localAgentId Internal identifier of the local agent
     */
    public deleteLocalAgent(localAgentId: string): Observable<void> {
        const url = `${this.localAgentsUrl}/${localAgentId}`;
        return this.httpClient.delete<void>(url, this.options);
    }

    // EXECUTION TRIGGERS

    /**
     * Get the list of execution triggers of a local agent
     * @param localAgentId Internal identifier of the local agent
     */
    public getExecutionTriggers(localAgentId: string): Observable<IOcbLocalAgentExecutionTrigger[]> {
        const url = `${this.localAgentsUrl}/${localAgentId}/ExecutionTriggers`;
        return this.httpClient.get<IOcbLocalAgentExecutionTrigger[]>(url, this.options);
    }

    /**
     * Add an execution trigger for a local agent
     * @param localAgentId Internal identifier of the local agent
     * @param executionTrigger Trigger information
     */
    public addExecutionTrigger(localAgentId: string, executionTrigger: IOcbCULocalAgentExecutionTrigger): Observable<IOcbLocalAgentExecutionTrigger> {
        const url = `${this.localAgentsUrl}/${localAgentId}/ExecutionTriggers`;
        return this.httpClient.post<IOcbLocalAgentExecutionTrigger>(url, executionTrigger, this.options);
    }

    /**
     * Get an execution trigger
     * @param localAgentId Internal identifier of the local agent
     * @param triggerId Internal identifier of the trigger
     */
    public getExecutionTrigger(localAgentId: string, triggerId: string): Observable<IOcbLocalAgentExecutionTrigger> {
        const url = `${this.localAgentsUrl}/${localAgentId}/ExecutionTriggers/${triggerId}`;
        return this.httpClient.get<IOcbLocalAgentExecutionTrigger>(url, this.options);
    }

    /**
     * Delete an execution trigger
     * @param localAgentId Internal identifier of the local agent
     * @param triggerId Internal identifier of the trigger
     */
    public deleteExecutionTrigger(localAgentId: string, triggerId: string): Observable<void> {
        const url = `${this.localAgentsUrl}/${localAgentId}/ExecutionTriggers/${triggerId}`;
        return this.httpClient.delete<void>(url, this.options);
    }

    // MAINTENANCES

    /**
     * Get the list of maintenance ranges of a local agent
     * @param localAgentId Internal identifier of the local agent
     */
    public getMaintenances(localAgentId: string): Observable<IOcbLocalAgentMaintenance[]> {
        const url = `${this.localAgentsUrl}/${localAgentId}/Maintenances`;
        return this.httpClient.get<IOcbLocalAgentMaintenance[]>(url, this.options);
    }

    /**
     * Add a maintenance range for a local agent
     * @param localAgentId Internal identifier of the local agent
     * @param maintenance Maintenance information
     */
    public addMaintenance(localAgentId: string, maintenance: IOcbCULocalAgentMaintenance): Observable<IOcbLocalAgentMaintenance> {
        const url = `${this.localAgentsUrl}/${localAgentId}/Maintenances`;
        return this.httpClient.post<IOcbLocalAgentMaintenance>(url, maintenance, this.options);
    }

    /**
     * Get a maintenance range
     * @param localAgentId Internal identifier of the local agent
     * @param maintenanceId Internal identifier of the maintenance range
     */
    public getMaintenance(localAgentId: string, maintenanceId: string): Observable<IOcbLocalAgentMaintenance> {
        const url = `${this.localAgentsUrl}/${localAgentId}/Maintenances/${maintenanceId}`;
        return this.httpClient.get<IOcbLocalAgentMaintenance>(url, this.options);
    }

    /**
     * Update a maintenance range
     * @param localAgentId Internal identifier of the local agent
     * @param maintenanceId Internal identifier of the maintenance range
     * @param maintenance Maintenance information
     */
    public updateMaintenance(localAgentId: string, maintenanceId: string, maintenance: IOcbCULocalAgentMaintenance): Observable<void> {
        const url = `${this.localAgentsUrl}/${localAgentId}/Maintenances/${maintenanceId}`;
        return this.httpClient.put<void>(url, maintenance, this.options);
    }

    /**
     * Delete a maintenance range
     * @param localAgentId Internal identifier of the local agent
     * @param maintenanceId Internal identifier of the maintenance range
     */
    public deleteMaintenance(localAgentId: string, maintenanceId: string): Observable<void> {
        const url = `${this.localAgentsUrl}/${localAgentId}/Maintenances/${maintenanceId}`;
        return this.httpClient.delete<void>(url, this.options);
    }

    // COMMANDS

    /**
     * Start a local agent
     * @param localAgentId Internal identifier of the local agent
     * @param waitForExecution {true} to wait for the execution of the command (default : {false})
     */
    public start(localAgentId: string, waitForExecution?: boolean): Observable<IOcbLocalAgentCommand> {

        if (!waitForExecution) { waitForExecution = false; }
        const url = `${this.localAgentsUrl}/${localAgentId}/Commands/Start?WaitForExecution=${waitForExecution}`;

        return this.httpClient.post<IOcbLocalAgentCommand>(url, this.options);
    }

    /**
     * Stop a local agent
     * @param localAgentId Internal identifier of the local agent
     * @param waitForExecution {true} to wait for the execution of the command (default : {false})
     */
    public stop(localAgentId: string, waitForExecution?: boolean): Observable<IOcbLocalAgentCommand> {

        if (!waitForExecution) { waitForExecution = false; }
        const url = `${this.localAgentsUrl}/${localAgentId}/Commands/Stop?WaitForExecution=${waitForExecution}`;

        return this.httpClient.post<IOcbLocalAgentCommand>(url, this.options);
    }

    /**
     * Restart a local agent
     * @param localAgentId Internal identifier of the local agent
     * @param waitForExecution {true} to wait for the execution of the command (default : {false})
     */
    public restart(localAgentId: string, waitForExecution?: boolean): Observable<IOcbLocalAgentCommand> {

        if (waitForExecution === undefined) { waitForExecution = false; }
        const url = `${this.localAgentsUrl}/${localAgentId}/Commands/Restart?WaitForExecution=${waitForExecution}`;

        return this.httpClient.post<IOcbLocalAgentCommand>(url, this.options);
    }

    /**
     * Get the list of queued commands of a local agent
     * @param localAgentId Internal identifier of the local agent
     * @param onlyNotCompleted {true} (default) to only return the commands which are not completed (done or error), otherwise {false}
     * @param utcDateFrom UTC Start date (time will be truncated)
     * @param utcDateTo UTC End date (time will be truncated)
     */
    public getCommands(localAgentId: string, onlyNotCompleted?: boolean, utcDateFrom?: string, utcDateTo?: string): Observable<IOcbLocalAgentCommand[]> {

        if (onlyNotCompleted === undefined) { onlyNotCompleted = true; }

        let url = `${this.localAgentsUrl}/${localAgentId}/Commands?OnlyNotCompleted=${onlyNotCompleted}`;

        if (utcDateFrom) { url = `${url}&UtcDateFrom=${utcDateFrom}`; }
        if (utcDateTo) { url = `${url}&UtcDateTo=${utcDateTo}`; }

        return this.httpClient.get<IOcbLocalAgentCommand[]>(url, this.options)
            .pipe(mergeMap((commands: IOcbLocalAgentCommand[]) => {
                if (commands.length > 0) {
                    return forkJoin(
                        commands.map((command: IOcbLocalAgentCommand) => {
                            if (command.requestedBy) {
                                if (command.requestedBy.indexOf('@') < 0) {
                                    command.requestedByUser = { displayName: command.requestedBy } as IGraphUser;
                                    return of(command);
                                }
                                return this.httpClient.get(`https://graph.microsoft.com/v1.0/users/${command.requestedBy}`)
                                    .pipe(map((gu: IGraphUser) => {
                                        command.requestedByUser = gu;
                                        return command;
                                    }),
                                        catchError(_ => {
                                            command.requestedByUser = { displayName: command.requestedBy } as IGraphUser;
                                            return of(command);
                                        }));
                            } else {
                                return of(command);
                            }
                        }),
                    );
                }
                else return of([] as IOcbLocalAgentCommand[]);
            }));
    }

    /**
     * Get a queued command of a local agent
     * @param localAgentId Internal identifier of the local agent
     * @param commandId Internal identifier of the command
     */
    public getCommand(localAgentId: string, commandId: string): Observable<IOcbLocalAgentCommand> {
        const url = `${this.localAgentsUrl}/${localAgentId}/Commands/${commandId}`;
        return this.httpClient.get<IOcbLocalAgentCommand>(url, this.options)
            .pipe(mergeMap((command: IOcbLocalAgentCommand) => {
                if (!command?.requestedBy) return of(command);
                if (command.requestedBy.indexOf('@') < 0) {
                    command.requestedByUser = { displayName: command.requestedBy } as IGraphUser;
                    return of(command);
                }
                return this.httpClient.get(`https://graph.microsoft.com/v1.0/users/${command.requestedBy}`)
                    .pipe(map((gu: IGraphUser) => {
                        command.requestedByUser = gu;
                        return command;
                    }),
                        catchError(_ => {
                            command.requestedByUser = { displayName: command.requestedBy } as IGraphUser;
                            return of(command);
                        }));
        }));
    }

    /**
     * Search through queued commands
     * @param environmentId Internal identifier of the environment
     * @param agentId Internal identifier of the agent
     * @param fileTransferId Internal identifier of the file transfer
     * @param onlyNotCompleted {true} (default) to only return the commands which are not completed (done or error), otherwise {false}
     * @param utcDateFrom UTC Start date (time will be truncated)
     * @param utcDateTo UTC End date (time will be truncated)
     */
    public searchCommands(environmentId?: string, agentId?: string, fileTransferId?: string, onlyNotCompleted?: boolean, utcDateFrom?: string, utcDateTo?: string): Observable<IOcbLocalAgentCommand[]> {

        if (onlyNotCompleted === undefined) { onlyNotCompleted = true; }

        let url = `${this.localAgentsUrl}/Commands/${environmentId}?OnlyNotCompleted=${onlyNotCompleted}`;

        if (agentId) { url = `${url}&agentId=${agentId}`; }
        if (fileTransferId) { url = `${url}&fileTransferId=${fileTransferId}`; }
        if (utcDateFrom) { url = `${url}&UtcDateFrom=${utcDateFrom}`; }
        if (utcDateTo) { url = `${url}&UtcDateTo=${utcDateTo}`; }

        return this.httpClient.get<IOcbLocalAgentCommand[]>(url, this.options)
            .pipe(mergeMap((commands: IOcbLocalAgentCommand[]) => {
                if (commands.length > 0) {
                    return forkJoin(
                        commands.map((command: IOcbLocalAgentCommand) => {
                            if (command.requestedBy) {
                                if (command.requestedBy.indexOf('@') < 0) {
                                    command.requestedByUser = { displayName: command.requestedBy } as IGraphUser;
                                    return of(command);
                                }
                                return this.httpClient.get(`https://graph.microsoft.com/v1.0/users/${command.requestedBy}`)
                                    .pipe(map((gu: IGraphUser) => {
                                        command.requestedByUser = gu;
                                        return command;
                                    }),
                                        catchError(_ => {
                                            command.requestedByUser = { displayName: command.requestedBy } as IGraphUser;
                                            return of(command);
                                        }));
                            } else {
                                return of(command);
                            }
                        }),
                    );
                }
                else return of([] as IOcbLocalAgentCommand[]);
            }));
    }

    // CHECK CONNECTION

    /**
     * Request the checking of the connection to a remote platform
     * @param agentId Internal identifier of the agent
     * @param remoteFolderPath Path to the folder on the remote platform
     * @param remotePlatformId (optional) Internal identifier of the remote platform
     * @param waitForExecution set true to wait for the end of the execution of the check
     * @param sftpEngine set the engine to use for testing connection to a SFTP platform
     */
    public checkConnection(agentId: string, remoteFolderPath: string, remotePlatformId?: string, waitForExecution: boolean = true, sftpEngine?: string): Observable<IOcbLocalAgentCommand> {
        const url = !sftpEngine
            ? `${this.localAgentsUrl}/${agentId}/Commands/CheckConnection/${remotePlatformId}?WaitForExecution=${waitForExecution}`
            : `${this.localAgentsUrl}/${agentId}/Commands/CheckConnection/${remotePlatformId}?WaitForExecution=${waitForExecution}&sftpEngine=${sftpEngine}`;
        const body = { value: remoteFolderPath };
        return this.httpClient.post<IOcbLocalAgentCommand>(url, body, this.options);
    }
}
